import React, { useState, useEffect, useCallback } from 'react';
import { QuizAttempt, IncompleteQuizzesCardProps } from '../types';
import { getIncompleteQuizzes } from '../services/quizService';
import { useUser } from '../contexts/UserContext';
import { useQuiz } from '../contexts/QuizContext';
import { useNavigate } from 'react-router-dom';
import { ExponentialProgressBar } from './ExponentialProgressBar';
import {
  Card,
  CardBody,
  Button,
  Badge
} from './BaseComponents';
import { BaseModal } from './BaseModal';

/**
 * IncompleteQuizzesCard Component
 * 
 * Displays a list of incomplete quiz attempts that the user can resume or abandon.
 * Shows progress information and provides actions for each incomplete quiz.
 */
export const IncompleteQuizzesCard: React.FC<IncompleteQuizzesCardProps> = ({
  onQuizResumed
}) => {
  const { currentUser } = useUser();
  const { resumeQuiz, abandonSpecificQuiz, isLoading } = useQuiz();
  const navigate = useNavigate();

  const [incompleteQuizzes, setIncompleteQuizzes] = useState<QuizAttempt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAbandonModal, setShowAbandonModal] = useState<string | null>(null);

  const fetchIncompleteQuizzes = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      const quizzes = await getIncompleteQuizzes(currentUser.userId);
      setIncompleteQuizzes(quizzes);
    } catch (err) {
      console.error('Error fetching incomplete quizzes:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch incomplete quizzes');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch incomplete quizzes on component mount
  useEffect(() => {
    fetchIncompleteQuizzes();
  }, [currentUser, fetchIncompleteQuizzes]);

  const handleResumeQuiz = async (attemptId: string) => {
    try {
      await resumeQuiz(attemptId);
      navigate(`/quiz/${attemptId}`);
      onQuizResumed?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resume quiz');
    }
  };

  const handleAbandonQuiz = async (attemptId: string) => {
    try {
      setError(null);

      // Abandon the specific quiz by its ID
      await abandonSpecificQuiz(attemptId);

      // Remove the abandoned quiz from our local state only after successful abandonment
      setIncompleteQuizzes(prev => prev.filter(quiz => quiz.attemptId !== attemptId));

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to abandon quiz');

      // Refresh the list to ensure consistency with the backend
      fetchIncompleteQuizzes();
    } finally {
      // Always close the modal, whether success or failure
      setShowAbandonModal(null);
    }
  };

  const calculateProgress = (quiz: QuizAttempt) => {
    if (!quiz.totalQuestions) return 0;
    // Use answeredQuestions for accurate progress calculation
    const answeredCount = quiz.answeredQuestions || 0;
    return Math.round((answeredCount / quiz.totalQuestions) * 100);
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading incomplete quizzes...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-8">
            <div className="text-red-500 mb-4">
              <svg className="mx-auto h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 mb-4">{error}</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={fetchIncompleteQuizzes}
            >
              Try Again
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (incompleteQuizzes.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-600">No incomplete quizzes found</p>
            <p className="text-sm text-gray-500 mt-1">All your quizzes are complete!</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card noHover={true}>
        <CardBody>

          <div className="space-y-3">
            {incompleteQuizzes.map((quiz) => {
              const progress = calculateProgress(quiz);
              return (
                <div
                  key={quiz.attemptId}
                  className="border rounded-lg p-4"
                >
                  <div className="flex items-center justify-between relative min-h-[80px]">
                    {/* Abandon button (X icon) on the left - centered vertically */}
                    <button
                      onClick={() => setShowAbandonModal(quiz.attemptId)}
                      disabled={isLoading}
                      className="absolute top-1/2 -translate-y-1/2 z-10 p-2 rounded-full hover:bg-red-50 text-red-500 hover:text-red-600 transition-colors duration-200 disabled:opacity-50"
                      title="Abandon quiz"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>

                    <div className="flex-1 px-12 py-2">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium text-gray-900">
                          {quiz.title || `Quiz #${quiz.attemptNumber}`}
                        </h4>
                        <Badge variant={quiz.difficulty === 'easy' ? 'success' : quiz.difficulty === 'medium' ? 'warning' : 'error'}>
                          {quiz.difficulty}
                        </Badge>
                      </div>

                      <div className="mt-4">
                        <ExponentialProgressBar
                          progress={progress / 100}
                          className="h-2 bg-gray-100 rounded-full"
                        />
                      </div>

                    </div>

                    {/* Resume button (chevron right icon) on the right - centered vertically */}
                    <button
                      onClick={() => handleResumeQuiz(quiz.attemptId)}
                      disabled={isLoading}
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full hover:bg-indigo-50 text-indigo-500 hover:text-indigo-600 transition-colors duration-200 disabled:opacity-50 group"
                      title="Resume quiz"
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent"></div>
                      ) : (
                        <svg className="h-5 w-5 text-neutral-400 group-hover:text-primary-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* Abandon Quiz Confirmation Modal */}
      {showAbandonModal && (
        <BaseModal
          isOpen={!!showAbandonModal}
          onClose={() => setShowAbandonModal(null)}
          showCloseButton={false}
        >
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">
                Are you sure you want to abandon this quiz?
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                This action cannot be undone. Your progress will be lost and you'll need to start a new quiz.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => setShowAbandonModal(null)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => handleAbandonQuiz(showAbandonModal)}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {isLoading ? 'Abandoning...' : 'Abandon Quiz'}
            </Button>
          </div>
        </BaseModal>
      )}
    </>
  );
};

export default IncompleteQuizzesCard;